import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, formatHour, SPORT_COLORS, hexToRgba } from "@/lib/utils";
import { getImageUrl } from "@/lib/images";
import BackButton from "@/components/BackButton";
import DatePicker from "@/components/DatePicker";
import { Search, MapPin, Users, Activity, Clock, Flame } from "lucide-react";
import { toast } from "sonner";

const SPORTS = ["football", "basketball", "tennis", "padel", "pickleball"];
const SPORT_DEFAULTS = { football: 14, basketball: 10, tennis: 2, padel: 4, pickleball: 2 };

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isPastSlot(hour, dateStr) {
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (dateStr < today) return true;
  if (dateStr > today) return false;
  const currentHour = d.getHours() + d.getMinutes() / 60;
  return hour < currentHour;
}

export default function PlayCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const prefilledCourtId = searchParams.get("court_id");
  const prefilledDate = searchParams.get("date");
  const prefilledStart = searchParams.get("start");
  const prefilledEnd = searchParams.get("end");
  const prefilledDuration = parseInt(searchParams.get("duration")) || 60;
  const hasPrefill = !!(prefilledCourtId && prefilledDate && prefilledStart && prefilledEnd);

  const [step, setStep] = useState(hasPrefill ? 2 : 1);
  const [selectedSport, setSelectedSport] = useState(null);
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [date, setDate] = useState(prefilledDate || "");
  const [slots, setSlots] = useState([]);
  const [pickedHours, setPickedHours] = useState([]);
  const [slotDuration, setSlotDuration] = useState(prefilledDuration);

  const [maxPlayers, setMaxPlayers] = useState(10);
  const [teamMode, setTeamMode] = useState(false);
  const [minPlayersPerClaim, setMinPlayersPerClaim] = useState(1);
  const [skillLevel, setSkillLevel] = useState("all");
  const [recurringWeekly, setRecurringWeekly] = useState(false);
  const [weeksAhead, setWeeksAhead] = useState(4);
  const [submitting, setSubmitting] = useState(false);
  const [initialLoading, setInitialLoading] = useState(hasPrefill);

  const prefillApplied = useRef(false);
  const slotsPrefilled = useRef(false);

  useEffect(() => {
    api.get("/courts").then((r) => setCourts(r.data.courts || r.data)).catch((e) => {
      if (import.meta.env.DEV) console.warn("courts load failed:", e);
    });
  }, []);

  useEffect(() => {
    if (!hasPrefill || prefillApplied.current) return;
    prefillApplied.current = true;
    api.get(`/courts/${prefilledCourtId}`).then((r) => {
      const court = r.data;
      setSelectedCourt(court);
      setSelectedSport(court.sport);
      setMaxPlayers(SPORT_DEFAULTS[court.sport] || 10);
      const isTeamSport = court.sport === "football" || court.sport === "basketball";
      setTeamMode(isTeamSport);
      setMinPlayersPerClaim(isTeamSport ? 5 : 1);
      setInitialLoading(false);
    }).catch(() => {
      setInitialLoading(false);
      toast.error(t("play.court_not_found"));
    });
  }, [hasPrefill, prefilledCourtId, t]);

  const loadAvailability = (courtId, selectedDate, dur) => {
    if (!courtId || !selectedDate) return;
    const duration = dur || slotDuration;
    api.get(`/courts/${courtId}/availability`, { params: { date: selectedDate, duration } })
      .then((r) => {
        const now = new Date();
        const today = todayStr();
        const currentHour = now.getHours() + now.getMinutes() / 60;
        const slotList = (r.data.slots || []).map((s) => {
          if (selectedDate < today) {
            return { ...s, available: false };
          }
          if (selectedDate === today && s.hour < currentHour) {
            return { ...s, available: false };
          }
          return s;
        });
        setSlots(slotList);
        setSlotDuration(r.data.slot_duration || duration);
        if (hasPrefill && prefilledStart && prefilledEnd && !slotsPrefilled.current) {
          const startH = parseFloat(prefilledStart);
          const endH = parseFloat(prefilledEnd);
          const inRange = slotList
            .filter((s) => s.available && s.hour >= startH && s.hour < endH)
            .map((s) => s.hour);
          if (inRange.length > 0) {
            setPickedHours(inRange);
            slotsPrefilled.current = true;
          }
        }
      })
      .catch((e) => {
        if (import.meta.env.DEV) console.warn("availability load failed:", e);
        setSlots([]);
      });
  };

  useEffect(() => {
    if (selectedCourt && date) {
      loadAvailability(selectedCourt.id, date, slotDuration);
    } else {
      setSlots([]);
    }
  }, [selectedCourt, date]);

  const handleDurationChange = (dur) => {
    setSlotDuration(dur);
    setPickedHours([]);
    if (selectedCourt && date) {
      loadAvailability(selectedCourt.id, date, dur);
    }
  };

  const hasPeakHours = (selectedCourt?.peak_hours || []).length > 0 && (selectedCourt?.peak_multiplier || 1) > 1;
  const hasWeekendPeak = (selectedCourt?.weekend_multiplier || 1) > 1;

  const filteredCourts = useMemo(() => {
    if (!selectedSport) return [];
    return courts.filter((c) => {
      if (c.sport !== selectedSport) return false;
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return c.name?.toLowerCase().includes(q) || c.city?.toLowerCase().includes(q);
    });
  }, [courts, selectedSport, searchQuery]);

  const toggleHour = (h, available) => {
    if (!available || isPastSlot(h, date)) return;
    const step = slotDuration / 60;
    setPickedHours((prev) => {
      if (prev.includes(h)) return prev.filter((x) => x !== h);
      const next = [...prev, h].sort((a, b) => a - b);
      for (let i = 1; i < next.length; i++) {
        if (Math.abs(next[i] - next[i - 1] - step) > 0.001) return [h];
      }
      return next;
    });
  };

  const handleSportSelect = (sport) => {
    setSelectedSport(sport);
    setSelectedCourt(null);
    setSearchQuery("");
    setDate("");
    setSlots([]);
    setPickedHours([]);
    setMaxPlayers(SPORT_DEFAULTS[sport] || 10);
    const isTeamSport = sport === "football" || sport === "basketball";
    setTeamMode(isTeamSport);
    setMinPlayersPerClaim(isTeamSport ? 5 : 1);
  };

  const handleSubmit = async () => {
    if (!selectedCourt || !date || pickedHours.length === 0) return;
    setSubmitting(true);
    try {
      const step = slotDuration / 60;
      const startH = pickedHours[0];
      const endH = pickedHours[pickedHours.length - 1] + step;
      await api.post("/play", {
        court_id: selectedCourt.id,
        date,
        start_hour: startH,
        end_hour: endH,
        max_players: maxPlayers,
        team_mode: teamMode,
        min_players_per_claim: teamMode ? minPlayersPerClaim : 1,
        skill_level: skillLevel,
        recurring_weekly: recurringWeekly,
        weeks_ahead: weeksAhead,
      });
      toast.success(t("play.created"));
      navigate("/play");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center">
        <div className="label-eyebrow animate-pulse">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-2xl mx-auto px-5 md:px-10 py-10">
        <BackButton className="mb-8" />

        <div className="label-eyebrow text-volt mb-2">{t("play.eyebrow")}</div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-8">{t("play.create_title")}</h1>

        <div className="flex gap-2 mb-6">
          <div className={`flex-1 h-1 transition-colors ${!hasPrefill && step >= 1 ? "bg-volt" : "bg-border/30"}`} />
          <div className={`flex-1 h-1 transition-colors ${step >= 2 ? "bg-volt" : "bg-border/30"}`} />
          <div className={`flex-1 h-1 transition-colors ${step >= 3 ? "bg-volt" : "bg-border/30"}`} />
        </div>

        {/* STEP 1: Pick sport + court (skip if pre-filled) */}
        {step === 1 && !hasPrefill && (
          <div>
            <label className="label-eyebrow block mb-4">{t("play.pick_sport")}</label>
            <div className="flex flex-wrap gap-2 mb-6">
              {SPORTS.map((sport) => (
                <button
                  key={sport}
                  onClick={() => handleSportSelect(sport)}
                  className={`flex items-center gap-2 px-5 py-3 border font-bold text-xs tracking-widest uppercase transition-all ${
                    selectedSport === sport
                      ? "text-primary-foreground"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  }`}
                  style={selectedSport === sport ? { background: SPORT_COLORS[sport]?.bg, borderColor: SPORT_COLORS[sport]?.bg, color: SPORT_COLORS[sport]?.text } : {}}
                >
                  <Activity className="h-4 w-4" />
                  {t("home.sports." + sport)}
                </button>
              ))}
            </div>

            {selectedSport && (
              <>
                <label className="label-eyebrow block mb-4">{t("play.pick_court")}</label>
                <div className="flex items-center gap-2 bg-card border border-border px-4 py-3 mb-4">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("play.search_courts")}
                    className="bg-transparent text-sm text-foreground/80 focus:outline-none w-full placeholder:text-muted-foreground/60"
                  />
                </div>
                <div className="max-h-72 overflow-y-auto space-y-1 border border-border">
                  {filteredCourts.length === 0 && (
                    <div className="p-8 text-center text-muted-foreground/60 text-sm">{t("play.no_courts")}</div>
                  )}
                  {filteredCourts.map((c) => {
                    const isSelected = selectedCourt?.id === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => setSelectedCourt(c)}
                        className={`w-full flex items-center gap-4 px-4 py-3 text-start border-b border-border/30 last:border-b-0 transition-colors ${
                          isSelected ? "bg-volt/5 border-s-2 border-s-volt" : "hover:bg-card"
                        }`}
                      >
                        <div className="w-12 h-12 bg-card overflow-hidden shrink-0 border border-border/30">
                          {c.images?.[0] && <img src={getImageUrl(c.images[0], 'thumbnail')} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm">{c.name}</div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                            <MapPin className="h-3 w-3" /> {c.city}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="font-heading text-xl text-volt">{formatPrice((c.slot_pricing || {})[String(c.default_slot_duration || c.slot_duration || 60)] ?? c.price_per_slot, c.currency)}</div>
                          <div className="text-[9px] text-muted-foreground/60">{t("play.per_hour")}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <div className="flex gap-3 mt-6">
              <button onClick={() => navigate("/play")} className="btn-outline rounded-sm px-6 py-3 text-xs flex-1">
                {t("common.cancel")}
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!selectedCourt}
                className="btn-volt rounded-sm px-6 py-3 text-xs flex-1 disabled:opacity-40"
              >
                {t("common.next")}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Pick date + time slots */}
        {(step === 2 || (step === 1 && hasPrefill)) && (
          <div>
            {selectedCourt && (
              <div className="flex items-center gap-2 border border-border bg-card px-4 py-3 mb-5">
                <div
                  className="w-8 h-8 flex items-center justify-center shrink-0"
                  style={{ background: hexToRgba(SPORT_COLORS[selectedCourt.sport]?.bg, 0.15) }}
                >
                  <Activity className="h-4 w-4" style={{ color: SPORT_COLORS[selectedCourt.sport]?.bg }} />
                </div>
                <span className="text-sm font-semibold truncate flex-1">{selectedCourt.name}</span>
                <button onClick={() => { setSelectedCourt(null); if (!hasPrefill) setStep(1); }} className="text-[10px] tracking-widest uppercase text-volt hover:underline shrink-0">
                  {t("play.change")}
                </button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
              <div className="w-full sm:flex-1">
                <DatePicker value={date} onChange={(v) => { setDate(v); setPickedHours([]); }} min={todayStr()} />
              </div>
              {(selectedCourt?.slot_durations || [30, 60, 90, 120]).length > 1 && (
                <div className="flex flex-wrap gap-1.5">
                  {(selectedCourt?.slot_durations || [30, 60, 90, 120]).map((dur) => (
                    <button
                      key={dur}
                      type="button"
                      onClick={() => handleDurationChange(dur)}
                      className={`px-3 py-2 text-[10px] tracking-widest uppercase font-bold border transition-all ${
                        slotDuration === dur ? "bg-volt text-primary-foreground border-volt" : "border-border text-muted-foreground hover:border-foreground/30"
                      }`}
                    >
                      {dur}{t("owner.court_form.min_short")}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {date && slots.length > 0 && (
              <div>
                <label className="label-eyebrow block mb-3">
                  {t("play.pick_slots")}
                  {pickedHours.length > 0 && (
                    <span className="text-muted-foreground font-normal ms-2">
                      {formatHour(pickedHours[0])} – {formatHour(pickedHours[pickedHours.length - 1] + slotDuration / 60)}
                    </span>
                  )}
                </label>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-80 overflow-y-auto">
                  {slots.map((s) => {
                    const picked = pickedHours.includes(s.hour);
                    const discountActive = s.has_discount && s.original_price > s.price;
                    return (
                      <button
                        key={s.hour}
                        type="button"
                        disabled={!s.available}
                        onClick={() => toggleHour(s.hour, s.available)}
                        className={`py-3 text-sm font-mono border transition-all relative ${
                          !s.available
                            ? "border-red-400/20 bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(248,113,113,0.06)_3px,rgba(248,113,113,0.06)_6px)] text-muted-foreground/60 cursor-not-allowed"
                            : picked
                            ? "border-volt bg-volt text-primary-foreground font-bold"
                            : "border-border hover:border-foreground/40"
                        }`}
                      >
                        {formatHour(s.hour)}
                        <div className="text-[10px] mt-0.5 text-muted-foreground">
                          {discountActive ? (
                            <span>
                              <span className="line-through opacity-50">{formatPrice(s.original_price, selectedCourt?.currency || "JOD")}</span>{" "}
                              <span className={picked ? "text-primary-foreground" : "text-volt"}>{formatPrice(s.price, selectedCourt?.currency || "JOD")}</span>
                            </span>
                          ) : (
                            formatPrice(s.price, selectedCourt?.currency || "JOD")
                          )}
                        </div>
                        {(s.is_peak || (hasWeekendPeak && s.is_weekend)) && (
                          <Flame className="absolute top-1 end-1 h-3 w-3 text-volt opacity-80" />
                        )}
                        {discountActive && s.available && (
                          <span className="absolute top-1 start-1 bg-volt text-primary-foreground text-[8px] px-1 py-[1px] font-bold leading-none">
                            -{Math.round((1 - s.price / s.original_price) * 100)}%
                          </span>
                        )}
                        {!s.available && (
                          <span className="absolute top-1 start-1 bg-red-400/80 text-white text-[7px] leading-none px-1 py-[1px] font-bold tracking-wider">
                            {t("court.booked")}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {date && slots.length === 0 && (
              <div className="border border-dashed border-border p-8 text-center">
                <Clock className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">{t("play.no_slots")}</p>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button onClick={() => !hasPrefill ? setStep(1) : navigate("/play")} className="btn-outline rounded-sm px-6 py-3 text-xs flex-1">
                {!hasPrefill ? t("common.back") : t("common.cancel")}
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!date || pickedHours.length === 0}
                className="btn-volt rounded-sm px-6 py-3 text-xs flex-1 disabled:opacity-40"
              >
                {t("common.next")}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Configure + Review */}
        {step === 3 && selectedCourt && (
          <div>
            <div className="space-y-5">
              <div>
                <label className="label-eyebrow block mb-2">{t("play.max_players")}</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={maxPlayers}
                  onChange={(e) => setMaxPlayers(parseInt(e.target.value) || 2)}
                  className="w-full bg-card border border-input px-4 py-3 text-sm focus:border-volt focus:outline-none"
                />
                <p className="text-[10px] text-muted-foreground mt-1">{t("play.max_players_hint")}</p>
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={teamMode}
                    onChange={(e) => { setTeamMode(e.target.checked); if (!e.target.checked) setMinPlayersPerClaim(1); }}
                    className="accent-volt w-4 h-4"
                  />
                  <div>
                    <span className="text-sm font-semibold">{t("play.team_mode")}</span>
                    <p className="text-[10px] text-muted-foreground">{t("play.team_mode_hint")}</p>
                  </div>
                </label>
              </div>

              {teamMode && (
                <div>
                  <label className="label-eyebrow block mb-2">{t("play.min_per_claim")}</label>
                  <input
                    type="number"
                    min={1}
                    max={maxPlayers}
                    value={minPlayersPerClaim}
                    onChange={(e) => setMinPlayersPerClaim(parseInt(e.target.value) || 1)}
                    className="w-full bg-card border border-input px-4 py-3 text-sm focus:border-volt focus:outline-none"
                  />
                </div>
              )}

              <div>
                <label className="label-eyebrow block mb-2">{t("play.skill_level")}</label>
                <div className="flex flex-wrap gap-2">
                  {["all", "beginner", "intermediate", "advanced"].map((lvl) => (
                    <button
                      key={lvl}
                      onClick={() => setSkillLevel(lvl)}
                      className={`px-4 py-2 text-[10px] tracking-widest uppercase font-bold border transition-all ${
                        skillLevel === lvl
                          ? "border-volt text-volt bg-volt/10"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {t("play.level_" + lvl)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={recurringWeekly}
                    onChange={(e) => setRecurringWeekly(e.target.checked)}
                    className="accent-volt w-4 h-4"
                  />
                  <div>
                    <span className="text-sm font-semibold">{t("play.recurring_weekly")}</span>
                    <p className="text-[10px] text-muted-foreground">{t("play.recurring_hint")}</p>
                  </div>
                </label>
                {recurringWeekly && (
                  <div className="ml-7">
                    <label className="label-eyebrow block mb-2">{t("play.weeks_ahead")}</label>
                    <input
                      type="number"
                      min={1}
                      max={12}
                      value={weeksAhead}
                      onChange={(e) => setWeeksAhead(Math.min(12, Math.max(1, parseInt(e.target.value) || 4)))}
                      className="w-24 bg-card border border-input px-4 py-3 text-sm text-center focus:border-volt focus:outline-none"
                    />
                  </div>
                )}
              </div>

              <div className="border border-border bg-card p-5">
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-3">{t("play.summary")}</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("play.court")}</span>
                    <span>{selectedCourt.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("play.date")}</span>
                    <span>{date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("play.time")}</span>
                    <span>{formatHour(pickedHours[0])} – {formatHour(pickedHours[pickedHours.length - 1] + slotDuration / 60)} ({pickedHours.length * slotDuration}m)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("play.players")}</span>
                    <span>{maxPlayers}</span>
                  </div>
                  {teamMode && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("play.team_mode")}</span>
                      <span>{t("play.on")}</span>
                    </div>
                  )}
                  <div className="border-t border-border pt-2 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{t("play.per_player")}</span>
                      <span className="font-heading text-2xl text-volt">
                        {formatPrice(
                          Math.round((((selectedCourt.slot_pricing || {})[String(selectedCourt.default_slot_duration || selectedCourt.slot_duration || 60)] || selectedCourt.price_per_slot) / maxPlayers * pickedHours.length) * 100) / 100,
                          selectedCourt.currency
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(2)} className="btn-outline rounded-sm px-6 py-3 text-xs flex-1">
                {t("common.back")}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-volt rounded-sm px-6 py-3 text-xs flex-1 disabled:opacity-40"
              >
                {submitting ? t("common.loading") : t("play.create_session")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
